'use client'

import { faClone, faPlus, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useIntl } from 'react-intl'
import { css, styled } from 'styled-components'

import { SingleLineWithEllipsis } from '@b-stock/fe-core/bstock-next/components'
import {
  Countdown,
  Button,
  ButtonSizes,
  CurrencyStringDollars,
} from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'

import Image from '@/components/Image'
import {
  toListingsBidOn,
  useCompareBadgeProps,
} from '@/components/listings/compare/compareAnalytics'
import { useCompareSelection } from '@/components/listings/compare/CompareSelectionProvider'
import { COMPARE_LIMIT } from '@/components/listings/compare/constants'
import StorefrontLogo from '@/components/listings/listingCard/BaseListingCard/ListingInfo/StorefrontLogo'
import { PageWidth } from '@/components/PageWidth'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

// Full-bleed bar: background/border/shadow span the viewport, while the inner
// content is constrained to PageWidth so the left cluster lines up with the
// filters column and the buttons line up with the right edge of the grid.
const Bar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 25;
  padding: 0.75rem 0;
  background-color: ${Colors.Semantic.Neutral.white};
  border-top: 1px solid ${Colors.Semantic.Neutral.medium_grey};
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
`

const Inner = styled(PageWidth)`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media ${Breakpoints.max.small} {
    flex-direction: column;
    align-items: stretch;
  }
`

const LeftCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
`

const IconBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  background-color: ${Colors.Semantic.BStock.backgroundSelected};
  color: ${Colors.Semantic.BStock.default.lightBackground};
`

const LeftText = styled.div`
  display: flex;
  flex-direction: column;
`

const Title = styled.span`
  ${Typography.Body3_Semibold}
  color: ${Colors.Semantic.Neutral.black};
`

const Subtext = styled.span`
  ${Typography.Body4}
  color: ${Colors.Semantic.Neutral.dark_grey};
`

const ThumbRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0.75rem;
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;

  /* The cards stretch to this row's content box, so without a bottom strip the
     horizontal scrollbar and the cards share the same pixels: an overlay
     scrollbar paints over the cards' bottom edge, and a classic one grows the
     row instead. Reserving the strip unconditionally also keeps the tray's
     height stable whether or not the row happens to overflow. */
  padding-bottom: 0.75rem;
`

// Thumbs and placeholders share one slot size so they fill the row as equal
// columns (capped by PageWidth, so ~14rem each at max width) and scale down /
// scroll on narrower screens.
const slot = css`
  flex: 1 1 0;
  /* Sized so the price + countdown line fits without truncating at typical
     values: the card spends 3rem on the thumbnail plus 1.25rem of padding and
     gap, leaving ~7.75rem for the text. ThumbMeta still ellipsizes, since no
     width fits every possible price. */
  min-width: 12.5rem;
`

const ThumbCard = styled.div`
  ${slot}
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  border: 1px solid ${Colors.Semantic.Neutral.medium_grey};
  border-radius: 0.5rem;

  > img {
    width: 3rem;
    height: 3rem;
    flex-shrink: 0;
    object-fit: cover;
    border-radius: 0.25rem;
  }
`

const ThumbInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
`

// Small breathing room under the seller logo before the listing title.
const SellerRow = styled.div`
  margin-bottom: 0.25rem;
`

const ThumbTitle = styled(SingleLineWithEllipsis)`
  ${Typography.Body4_Semibold}
  color: ${Colors.Semantic.Neutral.black};
`

// The slot is only 10rem wide and the thumbnail takes 3rem of it, so this line
// never fits at its natural width, and no slot width fixes that for every
// price. Same treatment as ThumbTitle above: truncate rather than spill past
// the card, with a tooltip carrying the full value.
const ThumbMeta = styled(SingleLineWithEllipsis)`
  ${Typography.Body4}
  color: ${Colors.Semantic.Neutral.dark_grey};
`

// Kept inside the card bounds: the scrolling ThumbRow (overflow-x: auto forces
// overflow-y to auto too) would clip a badge that overhangs the top edge.
const RemoveButton = styled.button`
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  border: none;
  border-radius: 50%;
  background-color: ${Colors.Semantic.Neutral.black};
  color: ${Colors.Semantic.Neutral.white};
  cursor: pointer;
`

const RightCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
`

// Reserves flow space at the bottom of the page so the fixed tray never covers
// the last row / pagination. It's a separate leaf so it (not the whole grid)
// is the only thing that re-renders as selections change. Taller on mobile,
// where Inner stacks into three rows instead of laying out in one.
const Spacer = styled.div`
  height: 7.75rem;

  @media ${Breakpoints.max.small} {
    height: 13.75rem;
  }
`

const Placeholder = styled.div`
  ${slot}
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  ${Typography.Body4}
  color: ${Colors.Semantic.Neutral.dark_grey};
  border: 1px dashed ${Colors.Semantic.Neutral.medium_grey};
  border-radius: 0.5rem;
`

/** Stable keys for the inert placeholder slots (no listing data to key on). */
const PLACEHOLDER_KEYS = Array.from(
  { length: COMPARE_LIMIT },
  (_, i) => `add-listing-${i + 1}`
)

export const CompareTray = () => {
  const { formatMessage } = useIntl()
  const { trackButtonClicked } = useAnalytics()
  const badgeProps = useCompareBadgeProps()
  const {
    compareMode,
    selectedIds,
    selectedListings,
    toggleListing,
    clearSelection,
    openPanel,
  } = useCompareSelection()

  if (!compareMode || selectedIds.length === 0) return null

  // Matches ComparePanel: a withheld price arrives as 0, which is a non-value
  // here rather than a literal $0.
  const empty = formatMessage({ id: 'Listings.ComparePanel.emptyValue' })

  const selectionProps = {
    listings_bid_on: toListingsBidOn(selectedListings),
    total_selected: selectedIds.length,
  }

  const handleClear = () => {
    trackButtonClicked({
      buttonName: 'clear_selected_listings',
      ...selectionProps,
      ...badgeProps(),
    })
    clearSelection()
  }

  const handleCompare = () => {
    trackButtonClicked({
      buttonName: 'compare_selected_listings',
      ...selectionProps,
      ...badgeProps(),
    })
    openPanel()
  }

  return (
    <Bar data-testid="compare-tray">
      <Inner>
        <LeftCluster>
          <IconBox>
            <FontAwesomeIcon icon={faClone} />
          </IconBox>
          <LeftText>
            <Title>
              {formatMessage({
                id: 'Listings.ListView.compareMode.tray.title',
              })}
            </Title>
            <Subtext>
              {formatMessage(
                { id: 'Listings.ListView.compareMode.tray.selected' },
                { count: selectedIds.length, limit: COMPARE_LIMIT }
              )}
            </Subtext>
          </LeftText>
        </LeftCluster>

        <ThumbRow>
          {selectedListings.map((listing) => (
            <ThumbCard key={listing.listingId} data-testid="compare-tray-thumb">
              <Image
                src={
                  listing.primaryImageUrl ||
                  '/home-portal/images/fallbackListingImage.png'
                }
                alt={formatMessage(
                  { id: 'Auction.image.alt' },
                  { title: listing.title }
                )}
                width={48}
                height={48}
              />
              <ThumbInfo>
                <SellerRow>
                  <StorefrontLogo listing={listing.originalSearchResult} />
                </SellerRow>
                <ThumbTitle>{listing.title}</ThumbTitle>
                <ThumbMeta>
                  <span>
                    {listing.currentPrice > 0 ? (
                      <CurrencyStringDollars
                        value={listing.currentPrice}
                        currency={listing.currency}
                        omitDecimal
                      />
                    ) : (
                      empty
                    )}
                  </span>
                  {' · '}
                  <Countdown target={listing.endTime} />
                </ThumbMeta>
              </ThumbInfo>
              <RemoveButton
                type="button"
                aria-label={formatMessage(
                  { id: 'Listings.ListView.compareMode.deselectCard' },
                  { title: listing.title }
                )}
                onClick={() => toggleListing(listing.listingId)}
              >
                <FontAwesomeIcon icon={faXmark} />
              </RemoveButton>
            </ThumbCard>
          ))}
          {PLACEHOLDER_KEYS.slice(0, COMPARE_LIMIT - selectedIds.length).map(
            (key) => (
              <Placeholder key={key}>
                <FontAwesomeIcon icon={faPlus} />
                {formatMessage({
                  id: 'Listings.ListView.compareMode.tray.addListing',
                })}
              </Placeholder>
            )
          )}
        </ThumbRow>

        <RightCluster>
          <Button
            appearance="secondary"
            size={ButtonSizes.small}
            onClick={handleClear}
          >
            {formatMessage({ id: 'Listings.ListView.compareMode.tray.clear' })}
          </Button>
          <Button
            appearance="primary"
            size={ButtonSizes.small}
            disabled={selectedIds.length < 2}
            onClick={handleCompare}
          >
            {formatMessage(
              { id: 'Listings.ListView.compareMode.tray.compareWithCount' },
              { count: selectedIds.length }
            )}
          </Button>
        </RightCluster>
      </Inner>
    </Bar>
  )
}

/**
 * Bottom-of-page spacer that reserves room for the fixed CompareTray. Rendered
 * as its own leaf inside the listings flow so that toggling a selection only
 * re-renders this tiny node and the tray — never the whole grid.
 */
export const CompareTraySpacer = () => {
  const { compareMode, selectedIds } = useCompareSelection()

  if (!compareMode || selectedIds.length === 0) return null

  return <Spacer aria-hidden data-testid="compare-tray-spacer" />
}
